import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Page, PageBody, PageHeader, PageContent } from "@corpuscomplete/web/components/layout";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Label } from "@corpuscomplete/web-ui/components/label";
import { Tabs, TabsList, TabsTrigger, TabsPanel } from "@corpuscomplete/web-ui/components/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@corpuscomplete/web-ui/components/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@corpuscomplete/web-ui/components/dialog";
import { adminRpc } from "@corpuscomplete/web/rpc/client";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  UserCheckIcon,
  UserXIcon,
  Trash2Icon,
} from "lucide-react";
import type { User } from "@corpuscomplete/core/auth/user";

type StatusFilter = User.Status | "all";
type InviteStatusFilter = "pending" | "used" | "all";

export function UsersPage() {
  return (
    <Page>
      <PageBody>
        <PageHeader>
          <Text variant="h5">User Management</Text>
        </PageHeader>
        <PageContent className="p-4">
          <Tabs defaultValue="users">
            <TabsList>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="invites">Invites</TabsTrigger>
            </TabsList>
            <TabsPanel value="users">
              <UsersTab />
            </TabsPanel>
            <TabsPanel value="invites">
              <InvitesTab />
            </TabsPanel>
          </Tabs>
        </PageContent>
      </PageBody>
    </Page>
  );
}

function UsersTab() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [cursors, setCursors] = useState<string[]>([]);
  const currentCursor = cursors[cursors.length - 1];

  const { data, isLoading, error } = useQuery(
    adminRpc.users.list.queryOptions({
      input: {
        status: statusFilter === "all" ? undefined : statusFilter,
        cursor: currentCursor,
        limit: 20,
      },
    }),
  );

  const activateMutation = useMutation(
    adminRpc.users.activate.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: adminRpc.users.key() });
      },
    }),
  );

  const deactivateMutation = useMutation(
    adminRpc.users.deactivate.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: adminRpc.users.key() });
      },
    }),
  );

  const handleStatusChange = useCallback((value: string) => {
    setStatusFilter(value as StatusFilter);
    setCursors([]);
  }, []);

  const handleNextPage = useCallback(() => {
    const cursor = data?.nextCursor;
    if (cursor) {
      setCursors((prev) => [...prev, cursor]);
    }
  }, [data?.nextCursor]);

  const handlePrevPage = useCallback(() => {
    setCursors((prev) => prev.slice(0, -1));
  }, []);

  const hasPrevPage = cursors.length > 0;
  const hasNextPage = Boolean(data?.nextCursor);

  return (
    <div className="flex flex-col gap-4 pt-4">
      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button variant="outline" size="sm">
                Status: {statusFilter === "all" ? "All" : capitalizeFirst(statusFilter)}
                <ChevronDownIcon className="ml-1 size-3" />
              </Button>
            }
          />
          <DropdownMenuContent>
            <DropdownMenuRadioGroup value={statusFilter} onValueChange={handleStatusChange}>
              <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="pending">Pending</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="active">Active</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isLoading && (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
      )}

      {error && (
        <div className="py-8 text-center text-sm text-destructive">
          Failed to load users: {error.message}
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No users found matching the current filters.
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  onActivate={() => activateMutation.mutate({ userId: user.id })}
                  onDeactivate={() => deactivateMutation.mutate({ userId: user.id })}
                  isActivating={activateMutation.isPending}
                  isDeactivating={deactivateMutation.isPending}
                />
              ))}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between">
            <Text variant="sm" className="text-muted-foreground">
              Showing {data.items.length} user{data.items.length !== 1 ? "s" : ""}
            </Text>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrevPage} disabled={!hasPrevPage}>
                <ChevronLeftIcon className="size-4" />
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={handleNextPage} disabled={!hasNextPage}>
                Next
                <ChevronRightIcon className="size-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface UserRowProps {
  user: {
    id: string;
    email: string;
    name: string;
    status: User.Status;
    createdAt: Date;
  };
  onActivate: () => void;
  onDeactivate: () => void;
  isActivating: boolean;
  isDeactivating: boolean;
}

function UserRow({ user, onActivate, onDeactivate, isActivating, isDeactivating }: UserRowProps) {
  const createdDate = user.createdAt.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return (
    <TableRow>
      <TableCell className="font-medium">{user.email}</TableCell>
      <TableCell>{user.name || "—"}</TableCell>
      <TableCell>
        <UserStatusBadge status={user.status} />
      </TableCell>
      <TableCell className="text-muted-foreground">{createdDate}</TableCell>
      <TableCell>
        {user.status === "pending" ? (
          <Button
            variant="ghost"
            size="xs"
            onClick={onActivate}
            disabled={isActivating}
            className="text-green-600 hover:text-green-700"
          >
            <UserCheckIcon className="mr-1 size-3" />
            Activate
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="xs"
            onClick={onDeactivate}
            disabled={isDeactivating}
            className="text-orange-600 hover:text-orange-700"
          >
            <UserXIcon className="mr-1 size-3" />
            Deactivate
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function UserStatusBadge({ status }: { status: User.Status }) {
  switch (status) {
    case "pending":
      return <Badge variant="outline">Pending</Badge>;
    case "active":
      return <Badge variant="default">Active</Badge>;
  }
}

function InvitesTab() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<InviteStatusFilter>("all");
  const [cursors, setCursors] = useState<string[]>([]);
  const currentCursor = cursors[cursors.length - 1];
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const { data, isLoading, error } = useQuery(
    adminRpc.users.listInvites.queryOptions({
      input: {
        status: statusFilter === "all" ? undefined : statusFilter,
        cursor: currentCursor,
        limit: 20,
      },
    }),
  );

  const revokeMutation = useMutation(
    adminRpc.users.revokeInvite.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: adminRpc.users.key() });
      },
    }),
  );

  const handleStatusChange = useCallback((value: string) => {
    setStatusFilter(value as InviteStatusFilter);
    setCursors([]);
  }, []);

  const handleNextPage = useCallback(() => {
    const cursor = data?.nextCursor;
    if (cursor) {
      setCursors((prev) => [...prev, cursor]);
    }
  }, [data?.nextCursor]);

  const handlePrevPage = useCallback(() => {
    setCursors((prev) => prev.slice(0, -1));
  }, []);

  const hasPrevPage = cursors.length > 0;
  const hasNextPage = Boolean(data?.nextCursor);

  return (
    <div className="flex flex-col gap-4 pt-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button variant="outline" size="sm">
                  Status: {statusFilter === "all" ? "All" : capitalizeFirst(statusFilter)}
                  <ChevronDownIcon className="ml-1 size-3" />
                </Button>
              }
            />
            <DropdownMenuContent>
              <DropdownMenuRadioGroup value={statusFilter} onValueChange={handleStatusChange}>
                <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="pending">Pending</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="used">Used</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger
            render={
              <Button size="sm">
                <PlusIcon className="mr-1 size-3" />
                Create Invite
              </Button>
            }
          />
          <CreateInviteDialog onClose={() => setIsCreateDialogOpen(false)} />
        </Dialog>
      </div>

      {isLoading && (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
      )}

      {error && (
        <div className="py-8 text-center text-sm text-destructive">
          Failed to load invites: {error.message}
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No invites found matching the current filters.
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((invite) => (
                <InviteRow
                  key={invite.id}
                  invite={invite}
                  onRevoke={() => revokeMutation.mutate({ inviteId: invite.id })}
                  isRevoking={revokeMutation.isPending}
                />
              ))}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between">
            <Text variant="sm" className="text-muted-foreground">
              Showing {data.items.length} invite{data.items.length !== 1 ? "s" : ""}
            </Text>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrevPage} disabled={!hasPrevPage}>
                <ChevronLeftIcon className="size-4" />
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={handleNextPage} disabled={!hasNextPage}>
                Next
                <ChevronRightIcon className="size-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function InviteRow({
  invite,
  onRevoke,
  isRevoking,
}: {
  invite: User.InviteInfo;
  onRevoke: () => void;
  isRevoking: boolean;
}) {
  const createdDate = invite.timeCreated.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const isPending = !invite.timeUsed;

  return (
    <TableRow>
      <TableCell className="font-medium">{invite.email}</TableCell>
      <TableCell>
        {isPending ? (
          <Badge variant="outline">Pending</Badge>
        ) : (
          <Badge variant="secondary">Used</Badge>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">{createdDate}</TableCell>
      <TableCell>
        {isPending && (
          <Button
            variant="ghost"
            size="xs"
            onClick={onRevoke}
            disabled={isRevoking}
            className="text-destructive hover:text-destructive"
          >
            <Trash2Icon className="mr-1 size-3" />
            Revoke
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function CreateInviteDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<{
    type: "user_activated" | "invite_created";
    message: string;
  } | null>(null);

  const createInviteMutation = useMutation(
    adminRpc.users.createInvite.mutationOptions({
      onSuccess: (data) => {
        void queryClient.invalidateQueries({ queryKey: adminRpc.users.key() });
        if (data.type === "user_activated") {
          setResult({
            type: "user_activated",
            message: `User ${data.user.email} has been activated.`,
          });
        } else {
          setResult({
            type: "invite_created",
            message: `Invite created for ${data.invite.email}.`,
          });
        }
        setEmail("");
      },
    }),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setResult(null);
      createInviteMutation.mutate({ email: email.trim() });
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Create Invite</DialogTitle>
        <DialogDescription>
          Enter an email address to invite a new user. If a pending user already exists with this
          email, they will be activated instead.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        {createInviteMutation.error && (
          <div className="text-sm text-destructive">{createInviteMutation.error.message}</div>
        )}

        {result && (
          <div
            className={`text-sm ${result.type === "user_activated" ? "text-green-600" : "text-blue-600"}`}
          >
            {result.message}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button type="submit" disabled={createInviteMutation.isPending || !email.trim()}>
            {createInviteMutation.isPending ? "Creating..." : "Create Invite"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
